import { Routes, UrlMatcher } from '@angular/router';
import { getSubStepTitle } from '@config/app-steps.config';
import {
    ApplicationInformationSubSteps,
    MainSteps,
    PaymentSubSteps,
    PersonalDetailsSubSteps,
    ProductSelectionSubSteps,
    SummarySubSteps,
} from '@models/config/app-structure.model';
import { applicationInitGuard } from 'app/guards/application-init.guard';

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const guidMatcher: UrlMatcher = (segments) => {
    if (segments.length !== 1 || !GUID_PATTERN.test(segments[0].path)) return null;
    return { consumed: segments, posParams: { token: segments[0] } };
};

import {
    ApplicationInformationRoutes,
    AppStepRoutes,
    PaymentRoutes,
    PersonalDetailsRoutes,
    ProductSelectionRoutes,
    SubmissionRoutes,
} from 'app/enums/config/app-routing.enum';

const personalDetailsRoutes: Routes = [
    {
        path: '',
        redirectTo: '/',
        pathMatch: 'full',
    },
    {
        path: `${PersonalDetailsRoutes.PERSONAL_INFORMATION}/:guid`,
        title: getSubStepTitle(MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.PERSONAL_INFORMATION),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import('@components/steps/personal-details-wrapper/substeps/personal-information/personal-information.component').then(
                (m) => m.PersonalInformationComponent,
            ),
    },
    // IAPP2-1741: Finance Information substep removed (all fields removed)
    // {
    //     path: `${PersonalDetailsRoutes.FINANCE_INFORMATION}/:guid`,
    //     canActivate: [applicationInitGuard],
    //     loadComponent: () =>
    //         import('@components/steps/personal-details-wrapper/substeps/finance-information/finance-information.component').then(
    //             (m) => m.FinanceInformationComponent,
    //         ),
    // },
    {
        path: `${PersonalDetailsRoutes.RESIDENTIAL_INFORMATION}/:guid`,
        title: getSubStepTitle(MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.RESIDENTIAL_INFORMATION),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import('@components/steps/personal-details-wrapper/substeps/residential-information/residential-information.component').then(
                (m) => m.ResidentialInformationComponent,
            ),
    },
    {
        path: `${PersonalDetailsRoutes.MAILING_ADDRESS}/:guid`,
        title: getSubStepTitle(MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.MAILING_ADDRESS),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import(
                '@components/steps/personal-details-wrapper/substeps/mailing-address-information/mailing-address-information.component'
            ).then((m) => m.MailingAddressInformationComponent),
    },
    {
        path: `${PersonalDetailsRoutes.CONTACT_INFORMATION}/:guid`,
        title: getSubStepTitle(MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.CONTACT_INFORMATION),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import('@components/steps/personal-details-wrapper/substeps/contact-information/contact-information.component').then(
                (m) => m.ContactInformationComponent,
            ),
    },
    {
        path: `${PersonalDetailsRoutes.RESIDENCY_AND_IDENTIFICATION}/:guid`,
        title: getSubStepTitle(MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.RESIDENCY_AND_IDENTIFICATION),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import(
                '@components/steps/personal-details-wrapper/substeps/residency-and-identification/residency-and-identification.component'
            ).then((m) => m.ResidencyAndIdentificationComponent),
    },
    {
        path: `${PersonalDetailsRoutes.IDENTITY_VERIFICATION_CONSENT}/:guid`,
        title: getSubStepTitle(MainSteps.PERSONAL_DETAILS, PersonalDetailsSubSteps.IDENTITY_VERIFICATION_CONSENT),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import(
                '@components/steps/personal-details-wrapper/substeps/identity-verification-consent/identity-verification-consent.component'
            ).then((m) => m.IdentityVerificationConsentComponent),
    },
    {
        path: '**',
        redirectTo: '/',
    },
];

const applicationInformationRoutes: Routes = [
    {
        path: '',
        redirectTo: '/',
        pathMatch: 'full',
    },
    {
        path: `${ApplicationInformationRoutes.OWNER}/:guid`,
        title: getSubStepTitle(MainSteps.APPLICATION_INFORMATION, ApplicationInformationSubSteps.OWNER),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import('@components/steps/application-information-wrapper/substeps/owner/owner.component').then((m) => m.OwnerComponent),
    },
    {
        path: `${ApplicationInformationRoutes.BENEFICIARY}/:guid`,
        title: getSubStepTitle(MainSteps.APPLICATION_INFORMATION, ApplicationInformationSubSteps.BENEFICIARY),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import('@components/steps/application-information-wrapper/substeps/beneficiary/beneficiary.component').then(
                (m) => m.BeneficiaryComponent,
            ),
    },
    {
        path: `${ApplicationInformationRoutes.EXISTING_INSURANCE}/:guid`,
        title: getSubStepTitle(MainSteps.APPLICATION_INFORMATION, ApplicationInformationSubSteps.EXISTING_INSURANCE),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import('@components/steps/application-information-wrapper/substeps/existing-insurance/existing-insurance.component').then(
                (m) => m.ExistingInsuranceComponent,
            ),
    },
    {
        path: `${ApplicationInformationRoutes.EXISTING_INSURANCE_REPLACEMENT}/:guid`,
        title: getSubStepTitle(MainSteps.APPLICATION_INFORMATION, ApplicationInformationSubSteps.EXISTING_INSURANCE_REPLACEMENT),
        canActivate: [applicationInitGuard],
        loadComponent: () =>
            import(
                '@components/steps/application-information-wrapper/substeps/existing-insurance-replacement/existing-insurance-replacement.component'
            ).then((m) => m.ExistingInsuranceReplacementComponent),
    },
    {
        path: '**',
        redirectTo: '/',
    },
];

const mainStepsRoutes: Routes = [
    {
        path: AppStepRoutes.PERSONAL_DETAILS,
        loadComponent: () =>
            import('@components/steps/personal-details-wrapper/personal-details-wrapper.component').then(
                (m) => m.PersonalDetailsWrapperComponent,
            ),
        children: personalDetailsRoutes,
    },
    {
        path: AppStepRoutes.PRODUCT_SELECTION,
        loadComponent: () =>
            import('@components/steps/product-selection-wrapper/product-selection-wrapper.component').then(
                (m) => m.ProductSelectionWrapperComponent,
            ),
        children: [
            {
                path: '',
                redirectTo: '/',
                pathMatch: 'full',
            },
            {
                path: `:guid`,
                title: getSubStepTitle(MainSteps.PRODUCT_SELECTION, ProductSelectionSubSteps.INTRO),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/product-selection-wrapper/substeps/intro/product-selection-intro.component').then(
                        (m) => m.ProductSelectionIntroComponent,
                    ),
            },
            {
                path: `${ProductSelectionRoutes.PRODUCT_SELECTION}/:guid`,
                title: getSubStepTitle(MainSteps.PRODUCT_SELECTION, ProductSelectionSubSteps.PRODUCT_SELECTION),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/product-selection-wrapper/substeps/product-selection/product-selection.component').then(
                        (m) => m.ProductSelectionComponent,
                    ),
            },
        ],
    },
    {
        path: AppStepRoutes.APPLICATION_INFORMATION,
        loadComponent: () =>
            import('@components/steps/application-information-wrapper/application-information-wrapper.component').then(
                (m) => m.ApplicationInformationWrapperComponent,
            ),
        children: applicationInformationRoutes,
    },
    {
        path: AppStepRoutes.SUMMARY,
        loadComponent: () => import('@components/steps/summary-wrapper/summary-wrapper.component').then((m) => m.SummaryWrapperComponent),
        children: [
            {
                path: '',
                redirectTo: '/',
                pathMatch: 'full',
            },
            {
                path: `:guid`,
                title: getSubStepTitle(MainSteps.SUMMARY, SummarySubSteps.SUMMARY),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/summary-wrapper/substeps/summary/summary.component').then((m) => m.SummaryComponent),
            },
        ],
    },
    {
        path: AppStepRoutes.PAYMENT,
        loadComponent: () => import('@components/steps/payment-wrapper/payment-wrapper.component').then((m) => m.PaymentWrapperComponent),
        children: [
            {
                path: '',
                redirectTo: '/',
                pathMatch: 'full',
            },
            {
                path: `${PaymentRoutes.INTRO}/:guid`,
                title: getSubStepTitle(MainSteps.PAYMENT, PaymentSubSteps.INTRO),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/payment-wrapper/substeps/intro/payment-intro.component').then(
                        (m) => m.PaymentIntroComponent,
                    ),
            },
            {
                path: `${PaymentRoutes.PAYMENT_INFORMATION}/:guid`,
                title: getSubStepTitle(MainSteps.PAYMENT, PaymentSubSteps.PAYMENT_INFORMATION),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/payment-wrapper/substeps/payment-information/payment-information.component').then(
                        (m) => m.PaymentInformationComponent,
                    ),
            },
            {
                path: `${PaymentRoutes.PRIMARY_PAYMENT_METHOD}/:guid`,
                title: getSubStepTitle(MainSteps.PAYMENT, PaymentSubSteps.PRIMARY_PAYMENT_METHOD),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/payment-wrapper/substeps/primary-payment-method/primary-payment-method.component').then(
                        (m) => m.PrimaryPaymentMethodComponent,
                    ),
            },
            {
                path: `${PaymentRoutes.SECONDARY_PAYMENT_METHOD}/:guid`,
                title: getSubStepTitle(MainSteps.PAYMENT, PaymentSubSteps.SECONDARY_PAYMENT_METHOD),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/payment-wrapper/substeps/secondary-payment-method/secondary-payment-method.component').then(
                        (m) => m.SecondaryPaymentMethodComponent,
                    ),
            },
            {
                path: `${PaymentRoutes.ACH_DISCLAIMER}/:guid`,
                title: getSubStepTitle(MainSteps.PAYMENT, PaymentSubSteps.ACH_DISCLAIMER),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/payment-wrapper/substeps/ach-disclaimer/ach-disclaimer.component').then(
                        (m) => m.AchDisclaimerComponent,
                    ),
            },
            {
                path: '**',
                redirectTo: '/',
            },
        ],
    },
    {
        path: AppStepRoutes.SUBMISSION,
        loadComponent: () =>
            import('@components/steps/submission-wrapper/submission-wrapper.component').then((m) => m.SubmissionWrapperComponent),
        children: [
            {
                path: '',
                redirectTo: '/',
                pathMatch: 'full',
            },
            {
                path: `${SubmissionRoutes.FINAL_CONSENTS}/:guid`,
                title: getSubStepTitle(MainSteps.SUBMISSION, PaymentSubSteps.FINAL_CONSENTS),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/payment-wrapper/substeps/final-consents/final-consents.component').then(
                        (m) => m.FinalConsentsComponent,
                    ),
            },
            {
                path: `${SubmissionRoutes.SUBMIT}/:guid`,
                title: getSubStepTitle(MainSteps.SUBMISSION, PaymentSubSteps.SUBMIT),
                canActivate: [applicationInitGuard],
                loadComponent: () =>
                    import('@components/steps/payment-wrapper/substeps/submit/submit.component').then((m) => m.SubmitComponent),
            },
            {
                path: '**',
                redirectTo: '/',
            },
        ],
    },
    {
        path: '**',
        redirectTo: '/',
    },
];

export const routes: Routes = [
    {
        path: '',
        title: 'Welcome',
        pathMatch: 'full',
        loadComponent: () => import('@components/welcome/welcome.component').then((m) => m.WelcomeComponent),
    },
    {
        path: 'education',
        title: 'Education',
        loadComponent: () => import('@components/education/education.component').then((m) => m.EducationComponent),
    },
    {
        path: 'privacy-policy',
        title: 'Privacy Policy',
        loadComponent: () => import('@components/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
    },
    {
        path: 'welcome',
        redirectTo: '',
        pathMatch: 'full',
    },
    {
        matcher: guidMatcher,
        title: 'Welcome',
        loadComponent: () => import('@components/welcome-landing/welcome-landing.component').then((m) => m.WelcomeLandingComponent),
    },
    {
        path: '',
        loadComponent: () => import('@layout/sidebar-layout/sidebar-layout').then((m) => m.SidebarLayoutComponent),
        children: [
            {
                path: '',
                loadComponent: () => import('@layout/stepper-layout/stepper-layout.component').then((m) => m.StepperLayoutComponent),
                children: mainStepsRoutes,
            },
        ],
    },
    {
        path: '**',
        redirectTo: '',
    },
];
